type LogLevel = 'info' | 'warn' | 'error';

interface LogMessage {
  timestamp: string;
  level: LogLevel;
  message: string;
  data?: any[];
}

class Logger {
  private static instance: Logger;
  private isDevelopment = import.meta.env.VITE_AWS_ENV === 'dev';

  private constructor() {}

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private serializeError(error: Error): Record<string, string> {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack || '',
    };
  }

  private replacer(_key: string, value: any): any {
    if (value instanceof Error) {
      return this.serializeError(value);
    }
    if (typeof value === 'symbol') {
      return value.toString();
    }
    if (value instanceof Set) {
      return Array.from(value);
    }
    if (value instanceof Map) {
      return Object.fromEntries(value);
    }
    if (typeof value === 'bigint') {
      return value.toString();
    }
    return value;
  }

  private formatLogMessage(level: LogLevel, message: string, ...args: any[]): LogMessage {
    const logMessage: LogMessage = {
      timestamp: new Date().toISOString(),
      level,
      message,
      data: args.length > 0 ? args.map(arg => this.safeSerialize(arg)) : undefined,
    };
    return logMessage;
  }

  private safeSerialize(arg: any): any {
    if (arg instanceof Error) {
      return this.serializeError(arg);
    }
    try {
      return JSON.parse(JSON.stringify(arg, this.replacer));
    } catch {
      return String(arg);
    }
  }

  private log(level: LogLevel, message: string, ...args: any[]) {
    if (this.isDevelopment) {
      const logMessage = this.formatLogMessage(level, message, ...args);
      console[level](
        `[${logMessage.timestamp}] [${level.toUpperCase()}] ${message}`,
        ...(logMessage.data || [])
      );
    }
  }

  info(message: string, ...args: any[]) {
    this.log('info', message, ...args);
  }

  warn(message: string, ...args: any[]) {
    this.log('warn', message, ...args);
  }

  error(message: string, ...args: any[]) {
    this.log('error', message, ...args);
  }
}

export const logger = Logger.getInstance();
